import React, { useEffect, useState } from "react";
import { Edit, useForm } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Form, Input, Switch, Button, Space, Card, Spin, message } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { useNavigate, useParams } from "react-router-dom";

export const CompanyEdit: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    // Use useShow to fetch the data
    const { queryResult } = useShow({
        resource: "company",
        id,
        meta: {
            fields: [
                "id", "name", "tradeName", "vatNumber", "tpin", "description",
                "taxNumber", "registrationNumber", "isActive",
                {
                    companyContacts: ["id", "phoneNumber", "email", "isPrimary"],
                    companyAddresses: ["id", "houseNo", "street", "city", "province", "country", "isPrimary"],
                    bankAccounts: ["id", "accountName", "accountNumber", "bankName", "branch", "swiftCode", "isPrimary"]
                }
            ],
        },
    });

    const { data, isLoading } = queryResult;
    const record = data?.data.value;

    const { formProps, saveButtonProps } = useForm({
        resource: "company",
        action: "edit",
        id,
        onMutationSuccess: () => {
            message.success("Company updated successfully");
            navigate("/company");
        }
    });

    // Update loading state
    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    if (loading || !record) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Spin />
            </div>
        );
    }

    return (
        <Edit saveButtonProps={saveButtonProps}>
            <Form 
                {...formProps} 
                layout="vertical"
                initialValues={{
                    id: record.id,
                    name: record.name || "",
                    tradeName: record.tradeName || "",
                    vatNumber: record.vatNumber || "",
                    tpin: record.tpin || "",
                    description: record.description || "",
                    taxNumber: record.taxNumber || "",
                    registrationNumber: record.registrationNumber || "",
                    isActive: record.isActive || false,
                    companyContacts: record.companyContacts || [],
                    companyAddresses: record.companyAddresses || [],
                    bankAccounts: record.bankAccounts || []
                }}
            >
                <Form.Item
                    hidden
                    name="id"
                >
                    <Input />
                </Form.Item>

                <Card title="Company Information" style={{ marginBottom: 16 }}>
                    <Form.Item
                        label="Company Name"
                        name="name"
                        rules={[{ required: true, message: 'Please input company name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Trade Name"
                        name="tradeName"
                        rules={[{ required: true, message: 'Please input trade name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="VAT Number"
                        name="vatNumber"
                        rules={[{ required: true, message: 'Please input VAT number' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="TIN Number"
                        name="tpin"
                        rules={[{ required: true, message: 'Please input TIN number' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Tax Number"
                        name="taxNumber"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Registration Number"
                        name="registrationNumber"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <Input.TextArea />
                    </Form.Item>

                    <Form.Item
                        label="Active"
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Card>

                <Card title="Bank Accounts" style={{ marginBottom: 16 }}>
                    <Form.List name="bankAccounts">
                        {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <Card 
                                        key={key} 
                                        type="inner" 
                                        title={`Bank Account ${index + 1}`}
                                        style={{ marginBottom: 16 }}
                                        extra={
                                            <Button
                                                type="link"
                                                danger
                                                onClick={() => remove(name)}
                                                icon={<MinusCircleOutlined />}
                                            >
                                                Remove
                                            </Button>
                                        }
                                    >
                                        <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'accountName']}
                                                label="Account Name"
                                                style={{ minWidth: 200 }}
                                            >
                                                <Input placeholder="Business Account" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'accountNumber']}
                                                label="Account Number"
                                                style={{ minWidth: 200 }}
                                                rules={[{ required: true, message: 'Account number is required' }]}
                                            >
                                                <Input placeholder="1234567890" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'bankName']}
                                                label="Bank Name"
                                                style={{ minWidth: 200 }}
                                                rules={[{ required: true, message: 'Bank name is required' }]}
                                            >
                                                <Input placeholder="Standard Bank" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'branch']}
                                                label="Branch"
                                                style={{ minWidth: 200 }}
                                            >
                                                <Input placeholder="Main Branch" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'swiftCode']}
                                                label="Swift Code"
                                                style={{ minWidth: 200 }}
                                            >
                                                <Input placeholder="SBZAZAJJ" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'isPrimary']}
                                                label="Primary Account"
                                                valuePropName="checked"
                                                style={{ minWidth: 150 }}
                                            >
                                                <Switch />
                                            </Form.Item>
                                        </Space>
                                    </Card>
                                ))}
                                {fields.length < 2 && (
                                    <Form.Item>
                                        <Button
                                            type="dashed"
                                            onClick={() => add({ isPrimary: fields.length === 0 })}
                                            block
                                            icon={<PlusOutlined />}
                                        >
                                            Add Bank Account (Max 2)
                                        </Button>
                                    </Form.Item>
                                )}
                                {fields.length === 2 && (
                                    <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                                        Maximum of 2 bank accounts allowed
                                    </div>
                                )}
                            </>
                        )}
                    </Form.List>
                </Card>

                <Card title="Contacts" style={{ marginBottom: 16 }}>
                    <Form.List name="companyContacts">
                        {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8, flexWrap: 'wrap' }} align="baseline">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'phoneNumber']}
                                            label="Phone Number"
                                            rules={[{ required: true, message: 'Phone number is required' }]}
                                        >
                                            <Input placeholder="Phone Number" style={{ width: 200 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'email']}
                                            label="Email"
                                            rules={[
                                                { required: true, message: 'Email is required' },
                                                { type: 'email', message: 'Please enter a valid email' }
                                            ]}
                                        >
                                            <Input placeholder="Email" style={{ width: 250 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'isPrimary']}
                                            label="Primary"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                        <Button 
                                            type="link" 
                                            danger 
                                            onClick={() => remove(name)}
                                            icon={<MinusCircleOutlined />}
                                        >
                                            Remove
                                        </Button>
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Add Contact
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Card title="Addresses">
                    <Form.List name="companyAddresses">
                        {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Card 
                                        key={key} 
                                        type="inner" 
                                        style={{ marginBottom: 16 }}
                                        extra={
                                            <Button
                                                type="link"
                                                danger
                                                onClick={() => remove(name)}
                                                icon={<MinusCircleOutlined />}
                                            >
                                                Remove
                                            </Button>
                                        }
                                    >
                                        <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'houseNo']}
                                                label="House No"
                                                rules={[{ required: true, message: 'House number is required' }]}
                                            >
                                                <Input placeholder="123" style={{ width: 120 }} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'street']}
                                                label="Street"
                                                rules={[{ required: true, message: 'Street is required' }]}
                                            >
                                                <Input placeholder="Main Street" style={{ width: 200 }} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'city']}
                                                label="City"
                                                rules={[{ required: true, message: 'City is required' }]}
                                            >
                                                <Input placeholder="Harare" style={{ width: 150 }} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'province']}
                                                label="Province"
                                                rules={[{ required: true, message: 'Province is required' }]}
                                            >
                                                <Input placeholder="Harare" style={{ width: 150 }} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'country']}
                                                label="Country"
                                                rules={[{ required: true, message: 'Country is required' }]}
                                            >
                                                <Input placeholder="Zimbabwe" style={{ width: 150 }} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'isPrimary']}
                                                label="Primary"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                        </Space>
                                    </Card>
                                ))}
                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Add Address
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Card>
            </Form>
        </Edit>
    );
};